import type { TSparseItem } from '@modules/items';
import type { AccessType } from '@modules/access/types';

export enum ERegistrationStatus {
  PLANNED = 'PLANNED',
  IN_PROGRESS = 'IN_PROGRESS',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  EXPIRED = 'EXPIRED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum ERegistrationValidity {
  EXPIRED = 'EXPIRED',
  NEXT_60_DAYS = 'NEXT_60_DAYS',
}

export enum ERegistrationWarning {
  PAST_VALIDITY_DATE = 'PAST_VALIDITY_DATE',
}

// Dates are ISO strings at UTC midnight. The backend omits a cleared date.
export type TRegistrationDetails = {
  _id: string;
  organization: string;
  registrationFolder: string;
  status: ERegistrationStatus;
  validFromDate?: string;
  validUntilDate?: string;
  reminderDate?: string;
  metadata: Record<string, unknown>;
  createdDate: string;
  createdByUser: string;
  updatedDate: string;
  updatedByUser?: string;
  folder: TSparseItem;
  country: TSparseItem | null;
  products: TSparseItem[];
  accessRight: AccessType;
  isFrozen: boolean;
  warnings: ERegistrationWarning[];
};

export type TRegistrationList = {
  items: TRegistrationDetails[];
  total: number;
  // Choices from every readable registration, independent of the current filters and page.
  filters: { countries: TSparseItem[]; products: TSparseItem[] };
};

export type TRegistrationDateKey = 'validFromDate' | 'validUntilDate' | 'reminderDate';

// Omitted fields stay unchanged; `null` clears one date or one metadata key.
export type TRegistrationUpdate = {
  status?: ERegistrationStatus;
  metadata?: Record<string, string | null>;
} & { [K in TRegistrationDateKey]?: string | null };

// Dates are `YYYY-MM-DD` calendar days, null when empty.
export type TRegistrationFormValues = {
  status: ERegistrationStatus;
  metadata: Record<string, string>;
} & { [K in TRegistrationDateKey]: string | null };

export type TRegistrationFilters = {
  search: string;
  countryIds: string[];
  productIds: string[];
  status: ERegistrationStatus[];
  validity?: ERegistrationValidity;
  validUntilFrom: string;
  validUntilTo: string;
};

// One-based page for the UI; the API hook converts it.
export type TRegistrationListParams = TRegistrationFilters & { page: number; pageSize: number };

export type TRegistrationScopeFilters = Pick<TRegistrationFilters, 'countryIds' | 'productIds'>;

export type TRegistrationExpirations = {
  months: { month: string; count: number }[];
};

export type TRegistrationSummary = {
  activeCountries: number;
  renewalsDue: number;
};

export type TRegistrationField = {
  // Stable technical key of the stored value. Labels may change; keys must never be reused for another meaning.
  key: string;
  label: string;
  type: 'text';
  searchable?: boolean;
  showInTable?: boolean;
};
